/**
 * Visibilidad de cards del Inicio — misma regla que
 * `opciones.module_permissions.dashboard_section_level`.
 */
import {
  MODULE_DASHBOARD,
  MODULE_PROVEEDORES,
  MODULE_REPORTES,
  MODULE_REMITOS_VTAS,
  MODULE_VENTAS,
  canSeeSection,
  canonicalizeModulePermissionEntries,
  getModulePermissionLevel,
  getSectionPermissionLevel,
  type ModulePermissionEntry,
} from "./modulePermissions";

export const DASHBOARD_SECTION_REMITOS_VTAS = "remitos_vtas";
export const DASHBOARD_SECTION_REMITOS_COMPRAS = "remitos_compras";
export const DASHBOARD_SECTION_FACTURAS = "facturas";
export const DASHBOARD_SECTION_ESTADISTICAS = "estadisticas";

/** Card del Inicio → módulo del menú que se pone Completo al tildar. Destildar no lo apaga. */
export const DASHBOARD_CARD_RELATED_MODULES: Record<string, number> = {
  [DASHBOARD_SECTION_FACTURAS]: MODULE_VENTAS,
  [DASHBOARD_SECTION_REMITOS_VTAS]: MODULE_REMITOS_VTAS,
  [DASHBOARD_SECTION_REMITOS_COMPRAS]: MODULE_PROVEEDORES,
  [DASHBOARD_SECTION_ESTADISTICAS]: MODULE_REPORTES,
};

export function dashboardCardVisible(
  modulePermissions: ModulePermissionEntry[] | undefined | null,
  sectionKey: string,
  opts?: { userRol?: string; isElevated?: boolean },
): boolean {
  if (opts?.isElevated) return true;
  const parent = getModulePermissionLevel(modulePermissions, MODULE_DASHBOARD);
  if (parent !== "none") {
    return canSeeSection(modulePermissions, MODULE_DASHBOARD, sectionKey);
  }
  // Sin 16: solo elevado. El nombre del rol no otorga cards.
  return false;
}

export function visibleDashboardCards(
  modulePermissions: ModulePermissionEntry[] | undefined | null,
  opts?: { userRol?: string; isElevated?: boolean },
) {
  return {
    remitosVtas: dashboardCardVisible(
      modulePermissions,
      DASHBOARD_SECTION_REMITOS_VTAS,
      opts,
    ),
    remitosCompras: dashboardCardVisible(
      modulePermissions,
      DASHBOARD_SECTION_REMITOS_COMPRAS,
      opts,
    ),
    facturas: dashboardCardVisible(
      modulePermissions,
      DASHBOARD_SECTION_FACTURAS,
      opts,
    ),
    stats: dashboardCardVisible(
      modulePermissions,
      DASHBOARD_SECTION_ESTADISTICAS,
      opts,
    ),
  };
}

function entryModuleId(entry: ModulePermissionEntry): number | null {
  const id = entry.moduleId ?? (entry as { module_id?: number }).module_id;
  if (id == null || Number.isNaN(Number(id))) return null;
  return Number(id);
}

function setModuleFull<T extends ModulePermissionEntry>(list: T[], moduleId: number): T[] {
  const existing = list.find((p) => entryModuleId(p) === moduleId);
  const entry = {
    ...(existing ?? { moduleId }),
    moduleId,
    permission: "full" as const,
    abmAccess: true,
    listadosAccess: true,
    actionColumn: true,
  } as T;
  if (existing) {
    return list.map((p) => (entryModuleId(p) === moduleId ? entry : p));
  }
  return [...list, entry];
}

/**
 * Si una card del Inicio está tildada, el módulo relacionado queda Completo.
 * Destildar no baja el módulo a none/read (puede seguir en el menú).
 */
export function ensureDashboardRelatedModulesFull<T extends ModulePermissionEntry>(
  list: T[] | undefined | null,
): T[] {
  const src = canonicalizeModulePermissionEntries(Array.isArray(list) ? [...list] : []);
  if (getModulePermissionLevel(src, MODULE_DASHBOARD) === "none") return src;
  let next = src;
  for (const [sectionKey, moduleId] of Object.entries(DASHBOARD_CARD_RELATED_MODULES)) {
    // Solo card ON → módulo full. Módulo full no vuelve a tildar la card.
    if (getSectionPermissionLevel(next, MODULE_DASHBOARD, sectionKey) === "none") continue;
    if (getModulePermissionLevel(next, moduleId) === "full") continue;
    next = setModuleFull(next, moduleId);
  }
  return next;
}
